/**
 * CSS do widget, injetado dentro do Shadow DOM.
 *
 * Nada aqui vaza para a página do cliente e nada de lá entra: é o Shadow DOM
 * que garante isso. Por consequência, não dá para usar Tailwind nem herdar
 * reset algum, então tudo é declarado explicitamente, inclusive `box-sizing` e
 * família de fonte.
 *
 * A paleta acompanha o design system do painel, mas os valores são literais em
 * vez de tokens importados: o widget precisa ser um arquivo autocontido.
 */

import {
  amber,
  coral,
  LAUNCHER_SIZES,
  launcherRadius as sharedLauncherRadius,
  mint,
  sky,
  readableInkOn,
  readableTextOn,
  WIDGET_DARK_BORDER,
  WIDGET_DARK_INK,
  WIDGET_DARK_MUTED,
  WIDGET_DARK_SURFACE,
  WIDGET_LIGHT_BORDER,
  WIDGET_LIGHT_INK,
  WIDGET_LIGHT_MUTED,
  WIDGET_LIGHT_SURFACE,
} from '@clixy/tokens';
import type { WidgetPosition, WidgetSettings } from './types';

export const WIDGET_Z_INDEX = 2147483000;

const POSITION_RULES: Record<WidgetPosition, string> = {
  'bottom-right': 'bottom: 20px; right: 20px;',
  'bottom-left': 'bottom: 20px; left: 20px;',
  'top-right': 'top: 20px; right: 20px;',
  'top-left': 'top: 20px; left: 20px;',
};

/** De que lado o painel abre, para não sair da tela. */
const PANEL_RULES: Record<WidgetPosition, string> = {
  'bottom-right': 'bottom: 84px; right: 20px;',
  'bottom-left': 'bottom: 84px; left: 20px;',
  'top-right': 'top: 84px; right: 20px;',
  'top-left': 'top: 84px; left: 20px;',
};

function launcherBox(settings: WidgetSettings): string {
  const size = LAUNCHER_SIZES[settings.launcher_size];
  if (settings.launcher_shape === 'circle') {
    // Quadrado perfeito e sem padding: o ícone é centralizado pelo flex, e
    // qualquer padding assimétrico deformaria o círculo.
    return `width: ${size.circle}; height: ${size.circle}; padding: 0;`;
  }
  return `padding: ${size.padding};`;
}

function launcherBorder(settings: WidgetSettings): string {
  if (settings.launcher_border_width === 0 || !settings.launcher_border_color) {
    return 'border: none;';
  }
  return `border: ${settings.launcher_border_width}px solid ${settings.launcher_border_color};`;
}

const LIGHT_SURFACE = WIDGET_LIGHT_SURFACE;
const DARK_SURFACE = WIDGET_DARK_SURFACE;

export function buildStyles(settings: WidgetSettings): string {
  const position = settings.position;
  const accentColor = settings.accent_color;

  // O cliente escolhe a cor de destaque sem saber onde ela vira texto. Estas
  // duas saem por contraste medido: uma para escrever sobre o destaque, outra
  // para escrever com o destaque sobre a superfície do painel.
  const onAccent = readableTextOn(accentColor);
  const accentInkLight = readableInkOn(accentColor, LIGHT_SURFACE);
  const accentInkDark = readableInkOn(accentColor, DARK_SURFACE);

  return `
:host {
  all: initial;
  --clixy-accent: ${accentColor};
  --clixy-on-accent: ${onAccent};
  --clixy-accent-ink: ${accentInkLight};
  --clixy-surface: ${LIGHT_SURFACE};
  --clixy-text: ${WIDGET_LIGHT_INK};
  --clixy-muted: ${WIDGET_LIGHT_MUTED};
  --clixy-border: ${WIDGET_LIGHT_BORDER};
  --clixy-danger: #d9534f;
  --clixy-radius: 16px;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}

@media (prefers-color-scheme: dark) {
  :host([data-theme='auto']) {
    --clixy-accent-ink: ${accentInkDark};
    --clixy-surface: ${DARK_SURFACE};
    --clixy-text: ${WIDGET_DARK_INK};
    --clixy-muted: ${WIDGET_DARK_MUTED};
    --clixy-border: ${WIDGET_DARK_BORDER};
  }
}

:host([data-theme='dark']) {
  --clixy-accent-ink: ${accentInkDark};
  --clixy-surface: ${DARK_SURFACE};
  --clixy-text: ${WIDGET_DARK_INK};
  --clixy-muted: ${WIDGET_DARK_MUTED};
  --clixy-border: ${WIDGET_DARK_BORDER};
}

*, *::before, *::after { box-sizing: border-box; }

.launcher {
  position: fixed; ${POSITION_RULES[position]}
  z-index: ${WIDGET_Z_INDEX};
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  ${launcherBox(settings)}
  ${launcherBorder(settings)}
  border-radius: ${sharedLauncherRadius(settings.launcher_shape, settings.launcher_corners)};
  background: var(--clixy-accent); color: ${settings.launcher_text_color};
  font: 600 ${LAUNCHER_SIZES[settings.launcher_size].font}/1 system-ui, -apple-system, sans-serif;
  cursor: pointer; touch-action: none;
  ${settings.launcher_shadow ? `box-shadow: 0 10px 28px ${accentColor}59;` : ''}
  transition: transform 140ms ease, box-shadow 140ms ease, width 200ms ease, height 200ms ease,
    padding 200ms ease, border-radius 200ms ease;
}
.launcher:hover { transform: translateY(-1px); ${
    settings.launcher_shadow ? `box-shadow: 0 14px 34px ${accentColor}70;` : ''
  } }

/* Sempre uma bolinha, com o tamanho pequeno já usado no formato circular:
   reaproveita a medida em vez de inventar uma nova, e vale para qualquer
   forma configurada, porque "recolhido" é um modo diferente de "expandido". */
.launcher.collapsed {
  width: ${LAUNCHER_SIZES.small.circle}; height: ${LAUNCHER_SIZES.small.circle};
  padding: 0; gap: 0; border-radius: 50%;
}
.launcher.collapsed svg { width: ${LAUNCHER_SIZES.small.icon}; height: ${LAUNCHER_SIZES.small.icon}; }

/* Arrastando, o deslocamento já segue o dedo a cada quadro: a transição do
   estado normal deixaria o botão correndo atrás do ponteiro. */
.launcher.dragging { transition: none; cursor: grabbing; }

/* O contorno de foco sai da cor do texto, e não da cor de destaque: com botão
   branco de borda escura, um foco na cor de destaque some contra o fundo. */
.launcher:focus-visible {
  outline: 3px solid ${settings.launcher_text_color}80;
  outline-offset: 3px;
}
.launcher svg { width: ${LAUNCHER_SIZES[settings.launcher_size].icon}; height: ${
    LAUNCHER_SIZES[settings.launcher_size].icon
  }; flex-shrink: 0; }

.launcher-label {
  display: inline-block; max-width: 240px; overflow: hidden; white-space: nowrap;
  transition: max-width 200ms ease;
}
.launcher.collapsed .launcher-label { max-width: 0; }

.panel {
  position: fixed; ${PANEL_RULES[position]}
  z-index: ${WIDGET_Z_INDEX};
  width: 360px; max-width: calc(100vw - 40px);
  max-height: calc(100vh - 120px); overflow-y: auto;
  background: var(--clixy-surface); color: var(--clixy-text);
  border: 1px solid var(--clixy-border); border-radius: var(--clixy-radius);
  box-shadow: 0 24px 60px rgba(24, 20, 45, 0.22);
  padding: 20px;
  animation: clixy-in 160ms ease-out;
}

@keyframes clixy-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 640px) {
  .panel {
    top: auto; left: 0; right: 0; bottom: 0;
    width: auto; max-width: none; max-height: 92vh;
    border-radius: 28px 28px 0 0;
    padding: 20px 20px 28px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .panel { animation: none; }
  .launcher, .launcher-label { transition: none; }
}

.header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.title { margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.02em; }
.back {
  border: none; background: none; padding: 0; cursor: pointer;
  color: var(--clixy-muted); font: 600 13px/1 system-ui, sans-serif;
}
.subtitle { margin: 6px 0 0; font-size: 12.5px; line-height: 1.5; color: var(--clixy-muted); }

.close {
  flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; padding: 0;
  border: none; border-radius: 8px; background: transparent;
  color: var(--clixy-muted); cursor: pointer;
}
.close:hover { background: var(--clixy-border); }
.close svg { width: 16px; height: 16px; }

.field { margin-top: 16px; }
.label { display: block; margin-bottom: 7px; font-size: 12px; font-weight: 600; }

.categories { display: flex; flex-direction: column; gap: 8px; margin-top: 16px; }
.category {
  display: flex; align-items: flex-start; gap: 12px;
  width: 100%; padding: 12px 14px; border: 1px solid var(--clixy-border); border-radius: 16px;
  background: transparent; color: var(--clixy-text);
  font: 500 14px/1.3 system-ui, sans-serif; cursor: pointer; text-align: left;
}
.category:hover { border-color: var(--clixy-accent); }
.category[aria-pressed='true'] {
  border-color: var(--clixy-accent); background: ${accentColor}14;
}
.category-copy { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.category-name { font-weight: 700; }
.category-hint { font-size: 12.5px; font-weight: 400; color: var(--clixy-muted); }
.dot {
  width: 10px; height: 10px; margin-top: 4px; border-radius: 50%; flex-shrink: 0;
  background: var(--clixy-accent);
}
.category[data-category='bug'] .dot { background: ${coral[500]}; }
.category[data-category='ui'] .dot { background: ${sky[500]}; }
.category[data-category='data'] .dot { background: ${amber[500]}; }
.category[data-category='idea'] .dot { background: ${mint[600]}; }

.textarea, .input {
  width: 100%; padding: 10px 12px;
  border: 1px solid var(--clixy-border); border-radius: 10px;
  background: transparent; color: var(--clixy-text);
  font: 400 13px/1.5 system-ui, sans-serif;
}
.textarea { min-height: 92px; resize: vertical; }
.textarea:focus, .input:focus { outline: 2px solid ${accentColor}66; outline-offset: 0; border-color: var(--clixy-accent); }

.point {
  width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  padding: 10px; border: 1px dashed var(--clixy-border); border-radius: 10px;
  background: transparent; color: var(--clixy-text);
  font: 500 12.5px/1 system-ui, sans-serif; cursor: pointer;
}
.point:hover { border-color: var(--clixy-accent); color: var(--clixy-accent-ink); }
.point svg { width: 15px; height: 15px; }

.picked {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 11px; border-radius: 10px; background: ${accentColor}14;
  font-size: 12px; color: var(--clixy-accent-ink);
}
.picked code {
  flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-family: ui-monospace, Menlo, monospace; font-size: 11px;
}

.preview { margin-top: 9px; width: 100%; border-radius: 10px; border: 1px solid var(--clixy-border); display: block; }

.error { margin-top: 12px; padding: 9px 11px; border-radius: 10px; background: #d9534f1a; color: var(--clixy-danger); font-size: 12px; }
.hint { margin: 10px 0 0; font-size: 11px; line-height: 1.5; color: var(--clixy-muted); }

.submit {
  width: 100%; margin-top: 16px; padding: 14px;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  border: none; border-radius: 999px;
  background: var(--clixy-accent); color: var(--clixy-on-accent);
  font: 700 16px/1 system-ui, sans-serif; cursor: pointer;
}
.submit svg { width: 16px; height: 16px; }
.submit:disabled { opacity: 0.55; cursor: not-allowed; }

.done { text-align: center; padding: 6px 0; }
.done-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 46px; height: 46px; border-radius: 50%;
  background: ${accentColor}1f; color: var(--clixy-accent-ink); margin-bottom: 12px;
}
.done-icon svg { width: 24px; height: 24px; }
.done h2 { margin: 0 0 6px; font-size: 15px; font-weight: 700; }
.done p { margin: 0; font-size: 13px; color: var(--clixy-muted); }
.done-actions { display: flex; align-items: center; justify-content: center; gap: 16px; }

.link {
  margin-top: 14px; border: none; background: none; padding: 0;
  color: var(--clixy-accent-ink); font: 500 13px/1 system-ui, sans-serif;
  cursor: pointer; text-decoration: underline;
}
.block-link { display: block; width: 100%; text-align: center; }
.brand { margin: 14px 0 0; text-align: center; font-size: 12px; color: var(--clixy-muted); }
.goes { margin-top: 16px; }
.goes ul { margin: 6px 0 0; padding-left: 18px; }
.goes li { font-size: 12.5px; line-height: 1.6; color: var(--clixy-muted); }
`;
}
